import { useCallback, useEffect, useState } from "react";
import { avisarError } from "../../shared/errores";
import { guardarTarea, listarProyectos, listarTareas, personas as listarPersonas } from "./proyectosService";
import { Avatar, Barra, Etiqueta } from "./componentes";
import DetalleProyecto from "./DetalleProyecto";
import { botonFantasma, botonPrimario, contenedor, panel, pestana, tituloPagina } from "./estilos";
import FormProyecto from "./FormProyecto";
import Esqueleto from "../../shared/components/Esqueleto";
import {
  ESTADOS_PROYECTO, SALUD, avance, datosDe, esAdministrador, estaVencida, fechaCorta, nombrePersona, salud,
} from "./reglas";

const FILTROS = [
  { id: "activos", label: "Activos", estados: ["planeado", "en_curso", "pausado"] },
  { id: "cerrados", label: "Cerrados", estados: ["completado", "cancelado"] },
  { id: "todos", label: "Todos", estados: null },
];

function TarjetaProyecto({ proyecto, tareas, onAbrir }) {
  const propias = tareas.filter((t) => t.proyecto_id === proyecto.id);
  const hechas = propias.filter((t) => t.estado === "completada").length;
  const porcentaje = avance(propias.length, hechas, proyecto.estado);
  const estado = datosDe(ESTADOS_PROYECTO, proyecto.estado);
  const idSalud = salud(proyecto, porcentaje, propias.some(estaVencida));
  const lider = nombrePersona(proyecto.lider);

  return (
    <button
      type="button"
      onClick={() => onAbrir(proyecto)}
      className={`group ${panel} flex flex-col overflow-hidden p-6 text-left transition-colors hover:bg-white/5`}
    >
      {proyecto.portada_url && (
        <div className="relative -mx-6 -mt-6 mb-5 h-40 overflow-hidden">
          <img src={proyecto.portada_url} alt="" loading="lazy" className="absolute inset-0 h-full w-full object-cover transition duration-700 group-hover:scale-105" />
          <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-black/20 to-transparent" />
        </div>
      )}
      <div className="mb-4 flex w-full flex-wrap items-center gap-2">
        <Etiqueta clase={estado.clase}>{estado.label}</Etiqueta>
        {idSalud !== "cerrado" && <Etiqueta clase={SALUD[idSalud].clase}>{SALUD[idSalud].label}</Etiqueta>}
        <span className="ml-auto text-[10px] font-bold uppercase tracking-widest text-gray-500">{proyecto.ministerio}</span>
      </div>
      <h3 className="mb-2 text-2xl font-black uppercase leading-tight tracking-tight">{proyecto.nombre}</h3>
      <p className="mb-6 line-clamp-2 text-sm text-gray-400">{proyecto.descripcion || "Sin descripción"}</p>
      <div className="mt-auto w-full">
        <div className="mb-2 flex items-center justify-between text-xs">
          <span className="text-gray-500">{hechas} de {propias.length} tareas</span>
          <span className="font-bold">{porcentaje}%</span>
        </div>
        <Barra porcentaje={porcentaje} className="mb-5" />
        <div className="flex items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-2">
            <Avatar nombre={lider} />
            <span className="truncate text-xs text-gray-300">{lider || "Sin líder"}</span>
          </div>
          <span className="whitespace-nowrap text-xs text-gray-500">{fechaCorta(proyecto.fecha_inicio)} – {fechaCorta(proyecto.fecha_fin)}</span>
        </div>
      </div>
    </button>
  );
}

// Proyectos de la iglesia: la lista, y el detalle de cada uno (?proyecto=ID en la dirección).
function Proyectos({ usuario, contexto = {}, irA }) {
  const [proyectos, setProyectos] = useState([]);
  const [tareas, setTareas] = useState([]);
  const [personas, setPersonas] = useState([]);
  const [cargando, setCargando] = useState(true);
  // Si la carga falla no se muestra "No hay proyectos": no sabemos si los hay.
  const [fallo, setFallo] = useState(false);
  const [filtro, setFiltro] = useState("activos");
  const [creando, setCreando] = useState(false);

  const cargar = useCallback(async () => {
    try {
      const [listaProyectos, listaTareas] = await Promise.all([listarProyectos(), listarTareas()]);
      setProyectos(listaProyectos);
      setTareas(listaTareas);
      setFallo(false);
    } catch (error) {
      setFallo(true);
      avisarError(error);
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    cargar();
    listarPersonas().then(setPersonas).catch(avisarError);
  }, [cargar]);

  // La tarjeta cambia de columna al instante; si el servidor lo rechaza, vuelve a su lugar.
  const moverTarea = async (tarea, estado) => {
    const antes = tareas;
    const completada_en = estado === "completada" ? tarea.completada_en || new Date().toISOString() : null;
    setTareas((lista) => lista.map((t) => (t.id === tarea.id ? { ...t, estado, completada_en } : t)));
    try {
      await guardarTarea(tarea.id, { estado });
      cargar();
    } catch (error) {
      setTareas(antes);
      avisarError(error);
    }
  };

  const abrir = (proyecto) => irA("proyectos", proyecto ? { proyectoId: proyecto.id } : {});
  const abierto = proyectos.find((p) => p.id === contexto.proyectoId);

  if (cargando) return <div className={contenedor}><Esqueleto tipo="tarjetas" texto="Cargando proyectos…" /></div>;

  if (fallo) {
    return (
      <div className={contenedor}>
        <h2 className={tituloPagina}>Proyectos</h2>
        <div className={`${panel} mt-8 p-12 text-center`}>
          <p className="mb-4 text-gray-400">No se pudieron cargar los proyectos.</p>
          <button type="button" onClick={() => { setCargando(true); cargar(); }} className={botonFantasma}>Reintentar</button>
        </div>
      </div>
    );
  }

  if (contexto.proyectoId) {
    if (!abierto) {
      return (
        <div className={contenedor}>
          <div className={`${panel} p-12 text-center`}>
            <p className="mb-4 text-gray-400">Este proyecto no existe o fue eliminado.</p>
            <button type="button" onClick={() => abrir(null)} className={botonFantasma}>Volver a proyectos</button>
          </div>
        </div>
      );
    }
    return (
      <DetalleProyecto
        proyecto={abierto}
        tareas={tareas}
        personas={personas}
        usuario={usuario}
        onVolver={() => abrir(null)}
        onCambio={cargar}
        onMoverTarea={moverTarea}
        onEliminado={() => { abrir(null); cargar(); }}
      />
    );
  }

  const { estados } = datosDe(FILTROS, filtro);
  const visibles = proyectos
    .filter((p) => !estados || estados.includes(p.estado))
    .sort((a, b) => (a.fecha_fin || "9999").localeCompare(b.fecha_fin || "9999"));

  return (
    <div className={contenedor}>
      <div className="mb-8 flex flex-wrap items-end justify-between gap-6">
        <div>
          <h2 className={tituloPagina}>Proyectos</h2>
        </div>
        {esAdministrador(usuario) && (
          <button type="button" onClick={() => setCreando(true)} className={botonPrimario}>+ Nuevo proyecto</button>
        )}
      </div>

      <div className="mb-6 flex gap-1">
        {FILTROS.map((f) => (
          <button key={f.id} type="button" onClick={() => setFiltro(f.id)} className={pestana(filtro === f.id)}>{f.label}</button>
        ))}
      </div>

      {visibles.length === 0 ? (
        <div className={`${panel} p-12 text-center text-gray-400`}>
          {proyectos.length ? "No hay proyectos en esta vista." : "Todavía no hay proyectos."}
          {!proyectos.length && esAdministrador(usuario) && " Crea el primero con «Nuevo proyecto»."}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
          {visibles.map((p) => <TarjetaProyecto key={p.id} proyecto={p} tareas={tareas} onAbrir={abrir} />)}
        </div>
      )}

      {creando && (
        <FormProyecto
          personas={personas}
          puedeCambiarLider
          onGuardado={(nuevo) => { setCreando(false); cargar().then(() => abrir(nuevo)); }}
          onCerrar={() => setCreando(false)}
        />
      )}
    </div>
  );
}

export default Proyectos;
